import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { getSiteConfig } from "@/lib/api";
import { Dna, Cpu, HeartPulse, Microscope, Leaf, Lightbulb } from "lucide-react";

const ICON_MAP = { Dna, Cpu, HeartPulse, Microscope, Leaf, Lightbulb };

export default function AboutSection() {
  const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    getSiteConfig("about").then(r => setCfg(r.data?.data || {})).catch(() => {});
  }, []);

  const overline = cfg?.overline || "Qui sommes-nous ?";
  const title = cfg?.title || "Unité de Recherche UR-EBioReZ";
  const p1 = cfg?.paragraph1 || "";
  const p2 = cfg?.paragraph2 || "";
  const imageUrl = cfg?.image_url || "https://images.unsplash.com/photo-1576089275954-40cd98bfcfdb?w=700&q=80";
  const affiliation = cfg?.affiliation || "";
  const features = cfg?.features || [];

  return (
    <section id="about" data-testid="about-section" className="py-24 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div ref={ref} initial={{ opacity: 0, x: -30 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7 }}>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#1670B9] mb-4">{overline}</p>
            <h2 className="font-['Quicksand'] text-3xl lg:text-4xl font-medium tracking-tight text-slate-800 mb-6" data-testid="about-title">
              {title}
            </h2>
            {p1 && <p className="text-base leading-relaxed text-slate-600 mb-4">{p1}</p>}
            {p2 && <p className="text-base leading-relaxed text-slate-600 mb-6">{p2}</p>}
            <div className="space-y-5">
              {features.map((f, i) => {
                const IconComp = ICON_MAP[f.icon] || Dna;
                return (
                  <motion.div key={`feat-${f.title}-${i}`} initial={{ opacity: 0, y: 15 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.2 + i * 0.15 }} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#1670B9]/10 flex items-center justify-center">
                      <IconComp className="w-5 h-5 text-[#1670B9]" />
                    </div>
                    <div>
                      <h3 className="font-['Quicksand'] text-base font-semibold text-slate-800 mb-1">{f.title}</h3>
                      <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7, delay: 0.2 }} className="relative">
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img src={imageUrl} alt="Recherche" className="w-full h-[400px] lg:h-[500px] object-cover" data-testid="about-image" />
            </div>
            {affiliation && (
              <div className="absolute -bottom-6 left-6 right-6 bg-white rounded-xl shadow-lg p-4 border border-slate-100">
                <p className="text-xs uppercase tracking-wider text-[#1670B9] font-bold mb-1">Affiliation</p>
                <p className="text-sm text-slate-700 font-medium">{affiliation}</p>
              </div>
            )}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#F49C03]/15 rounded-xl -z-10" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
